import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { 
  adminGrupoService, 
  adminTrilhaService, 
  adminUserService 
} from '../../shared/services';
import { 
  FolderOpen, 
  Plus, 
  Trash2,
  BookOpen,
  Users,
  ArrowLeft,
  Edit2,
  Star,
  MoveVertical
} from 'lucide-react';

export function AdminGrupoDetalhePage() {
  const { empresaId, grupoId } = useParams<{ empresaId: string; grupoId: string }>();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'trilhas' | 'usuarios'>('trilhas');

  // Modais
  const [showAddTrilha, setShowAddTrilha] = useState(false);
  const [showAddUsuario, setShowAddUsuario] = useState(false);
  const [selectedTrilhaId, setSelectedTrilhaId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [trilhaCustomization, setTrilhaCustomization] = useState({
    displayOrder: 1,
    isRequired: false,
    customName: '',
    customDescription: '',
  });

  const { data: grupo } = useQuery({
    queryKey: ['admin-grupo', grupoId],
    queryFn: () => adminGrupoService.getGrupo(grupoId!),
    enabled: !!grupoId,
  });

  const { data: trilhasDoGrupo, isLoading: loadingTrilhas } = useQuery({
    queryKey: ['admin-grupo-trilhas', grupoId],
    queryFn: () => adminGrupoService.getTrilhasDoGrupo(grupoId!),
    enabled: !!grupoId,
  });

  const { data: usuariosDoGrupo, isLoading: loadingUsuarios } = useQuery({
    queryKey: ['admin-grupo-usuarios', grupoId],
    queryFn: () => adminGrupoService.getUsuariosDoGrupo(grupoId!),
    enabled: !!grupoId,
  });

  const { data: todasTrilhas } = useQuery({
    queryKey: ['admin-trilhas'],
    queryFn: () => adminTrilhaService.getTrilhas({ page: 1 }),
  });

  const { data: todosUsuarios } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminUserService.getUsers({ page: 1 }),
  });

  const addTrilhaMutation = useMutation({
    mutationFn: () => adminGrupoService.addTrilhaAoGrupo(grupoId!, {
      trilhaId: selectedTrilhaId,
      ...trilhaCustomization,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-grupo-trilhas', grupoId] });
      setShowAddTrilha(false);
      setSelectedTrilhaId('');
      setTrilhaCustomization({ displayOrder: 1, isRequired: false, customName: '', customDescription: '' });
    },
  });

  const removeTrilhaMutation = useMutation({
    mutationFn: (trilhaId: string) => adminGrupoService.removeTrilhaDoGrupo(grupoId!, trilhaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-grupo-trilhas', grupoId] });
    },
  });

  const addUsuarioMutation = useMutation({
    mutationFn: () => adminGrupoService.addUsuarioAoGrupo(grupoId!, selectedUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-grupo-usuarios', grupoId] });
      setShowAddUsuario(false);
      setSelectedUserId('');
    },
  });

  const removeUsuarioMutation = useMutation({
    mutationFn: (userId: string) => adminGrupoService.removeUsuarioDoGrupo(grupoId!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-grupo-usuarios', grupoId] });
    },
  });

  const trilhasDisponiveis = todasTrilhas?.filter(
    (t: any) => !trilhasDoGrupo?.some((gt: any) => gt.trilhaId === t.id)
  ) || [];

  const usuariosDisponiveis = todosUsuarios?.filter(
    (u: any) => !usuariosDoGrupo?.some((gu: any) => gu.userId === u.id)
  ) || [];

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={`/admin/empresas/${empresaId}/grupos`}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para Grupos
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <FolderOpen className="w-8 h-8 text-blue-600" />
                {grupo?.nome || 'Gerenciar Grupo'}
              </h1>
              <p className="text-gray-600 mt-2">
                {grupo?.descricao || 'Configure trilhas e usuários do grupo'}
              </p>
            </div>
            <Link
              to={`/admin/empresas/${empresaId}/grupos/${grupoId}/editar`}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Edit2 className="w-5 h-5" />
              Editar Grupo
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('trilhas')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'trilhas'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Trilhas ({trilhasDoGrupo?.length || 0})
                </div>
              </button>
              <button
                onClick={() => setActiveTab('usuarios')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'usuarios'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Users className="w-5 h-5" />
                  Usuários ({usuariosDoGrupo?.length || 0})
                </div>
              </button>
            </div>
          </div>

          {/* Conteúdo Trilhas */}
          {activeTab === 'trilhas' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Trilhas do Grupo</h2>
                <button
                  onClick={() => setShowAddTrilha(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Adicionar Trilha
                </button>
              </div>

              {loadingTrilhas ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : trilhasDoGrupo && trilhasDoGrupo.length > 0 ? (
                <div className="space-y-3">
                  {trilhasDoGrupo
                    .sort((a: any, b: any) => a.displayOrder - b.displayOrder)
                    .map((grupoTrilha: any) => (
                      <div
                        key={grupoTrilha.id}
                        className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <MoveVertical className="w-5 h-5 text-gray-400" />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">
                            {grupoTrilha.customName || grupoTrilha.trilha?.nome || 'Trilha'}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {grupoTrilha.customDescription || grupoTrilha.trilha?.descricao || ''}
                          </p>
                          <div className="flex gap-2 mt-2">
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                              Ordem: {grupoTrilha.displayOrder}
                            </span>
                            {grupoTrilha.isRequired && (
                              <span className="flex items-center gap-1 text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded">
                                <Star className="w-3 h-3" />
                                Obrigatória
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => removeTrilhaMutation.mutate(grupoTrilha.trilhaId)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remover"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma trilha adicionada ao grupo</p>
                </div>
              )}
            </div>
          )}

          {/* Conteúdo Usuários */}
          {activeTab === 'usuarios' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Usuários do Grupo</h2>
                <button
                  onClick={() => setShowAddUsuario(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Adicionar Usuário
                </button>
              </div>

              {loadingUsuarios ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : usuariosDoGrupo && usuariosDoGrupo.length > 0 ? (
                <div className="space-y-3">
                  {usuariosDoGrupo.map((grupoUsuario: any) => (
                    <div
                      key={grupoUsuario.id}
                      className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {grupoUsuario.user?.nome || grupoUsuario.user?.name || 'Usuário'}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {grupoUsuario.user?.email}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Adicionado em: {new Date(grupoUsuario.joinedAt).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <button
                        onClick={() => removeUsuarioMutation.mutate(grupoUsuario.userId)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remover"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum usuário adicionado ao grupo</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Adicionar Trilha */}
        {showAddTrilha && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Adicionar Trilha ao Grupo</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selecionar Trilha *
                  </label>
                  <select
                    value={selectedTrilhaId}
                    onChange={(e) => setSelectedTrilhaId(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Escolha uma trilha...</option>
                    {trilhasDisponiveis.map((trilha: any) => (
                      <option key={trilha.id} value={trilha.id}>
                        {trilha.nome}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ordem de Exibição
                    </label>
                    <input
                      type="number"
                      value={trilhaCustomization.displayOrder}
                      onChange={(e) => setTrilhaCustomization(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 1 }))}
                      min="1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={trilhaCustomization.isRequired}
                        onChange={(e) => setTrilhaCustomization(prev => ({ ...prev, isRequired: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Trilha Obrigatória</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome Personalizado (opcional)
                  </label>
                  <input
                    type="text"
                    value={trilhaCustomization.customName}
                    onChange={(e) => setTrilhaCustomization(prev => ({ ...prev, customName: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Deixe vazio para usar o nome original"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição Personalizada (opcional)
                  </label>
                  <textarea
                    value={trilhaCustomization.customDescription}
                    onChange={(e) => setTrilhaCustomization(prev => ({ ...prev, customDescription: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Deixe vazio para usar a descrição original"
                  />
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex gap-3">
                <button
                  onClick={() => addTrilhaMutation.mutate()}
                  disabled={!selectedTrilhaId || addTrilhaMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Adicionar Trilha
                </button>
                <button
                  onClick={() => {
                    setShowAddTrilha(false);
                    setSelectedTrilhaId('');
                    setTrilhaCustomization({ displayOrder: 1, isRequired: false, customName: '', customDescription: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Adicionar Usuário */}
        {showAddUsuario && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Adicionar Usuário ao Grupo</h3>
              </div>
              <div className="p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecionar Usuário *
                </label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Escolha um usuário...</option>
                  {usuariosDisponiveis.map((usuario: any) => (
                    <option key={usuario.id} value={usuario.id}>
                      {usuario.nome || usuario.name} ({usuario.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="p-6 border-t border-gray-200 flex gap-3">
                <button
                  onClick={() => addUsuarioMutation.mutate()}
                  disabled={!selectedUserId || addUsuarioMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Adicionar Usuário
                </button>
                <button
                  onClick={() => {
                    setShowAddUsuario(false);
                    setSelectedUserId('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
